import { useState, useEffect, lazy, Suspense, useCallback, useRef } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { RefreshCw, Download, Crown, Search, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import type { GameData, HitData, SavedHighscore } from '@/types/game';
import { formatGameNumber, parseGameDate, isRateLimitError, normalizeGameData } from '@/utils/game-utils';
import { buildPlayerPhotoUrl } from '@/utils/player-photos';
import { computePlayerStats } from '@/utils/player-stats';

import { ShareButtons } from '@/components/highscore/ShareButtons';
import { PlayerCard } from '@/components/highscore/PlayerCard';
import { TeamRankings } from '@/components/highscore/TeamRankings';
import { HitMatrix } from '@/components/highscore/HitMatrix';
import { RecordsSection } from '@/components/highscore/RecordsSection';
import { Confetti } from '@/components/Confetti';


const TournamentMode = lazy(() => import('./TournamentMode').then(m => ({ default: m.TournamentMode })));

const RETENTION_GAMES = 25;

/** "DD.MM.YYYY - HH:MM" -> { date, time } für die Kopfzeile. */
const parseTimestampParts = (timestamp?: string): { date: string; time: string } => {
  const match = String(timestamp ?? '').match(/(\d{2}\.\d{2}\.\d{4})\s*-\s*(\d{2}:\d{2})/);
  if (match) return { date: match[1], time: `${match[2]} Uhr` };
  return { date: String(timestamp ?? ''), time: '' };
};
/**
 * Unterscheidet "Spiel existiert nicht" (404 / Erfolgsantwort ohne Spieldaten)
 * von echten Störungen (5xx, Netzwerk, Timeout). Die technische Ursache bleibt
 * in `cause` und wandert nur in die Konsole, nie in den Toast.
 */
class GameFetchError extends Error {
  constructor(public kind: 'not_found' | 'unavailable', public technical?: unknown) {
    super(kind);
    this.name = 'GameFetchError';
  }
}

/** Status der Edge-Function-Antwort aus dem FunctionsHttpError herausziehen. */
const errorStatus = (error: unknown): number | undefined => {
  const ctx = (error as { context?: { status?: number } })?.context;
  return typeof ctx?.status === 'number' ? ctx.status : undefined;
};



export const HighscoreBoard = () => {
  const [gameData, setGameData] = useState<GameData | null>(null);
  const [hitData, setHitData] = useState<HitData[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [savedHighscores, setSavedHighscores] = useState<SavedHighscore[]>([]);
  const [selectedHighscoreId, setSelectedHighscoreId] = useState<string>('live');
  const [hasManualSelection, setHasManualSelection] = useState(false);
  const [showTournamentMode, setShowTournamentMode] = useState(false);
  const [manualGameNumber, setManualGameNumber] = useState('');
  const [loadingManualGame, setLoadingManualGame] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [activeSection, setActiveSection] = useState<'ranking' | 'hits' | 'teams' | 'records'>('ranking');

  const gameDataRef = useRef<GameData | null>(null);
  gameDataRef.current = gameData;

  const deepLinkHandled = useRef(false);
  const manualSelectionRef = useRef(false);
  manualSelectionRef.current = hasManualSelection;


  const { toast } = useToast();


  // --- Data fetching helpers ---

  const fetchViaProxy = useCallback(async (url: string): Promise<string> => {
    const { data, error } = await supabase.functions.invoke('fetch-game-data', {
      body: { url }
    });
    if (error) {
      const status = errorStatus(error);
      console.error('fetch-game-data failed:', status ?? '(kein Status)', error);
      throw new GameFetchError(status === 404 ? 'not_found' : 'unavailable', error);
    }
    // Erfolgsantwort ohne Nutzdaten = Spiel nicht vorhanden
    if (!data?.data) throw new GameFetchError('not_found', data);
    return data.data;
  }, []);


  const parseJsonResponse = (raw: string): GameData => {
    try {
      return JSON.parse(raw);
    } catch {
      const start = raw.indexOf('{');
      const end = raw.lastIndexOf('}');
      if (start !== -1 && end !== -1 && end > start) {
        return JSON.parse(raw.slice(start, end + 1));
      }
      throw new Error('Antwort enthält kein parsebares JSON');
    }
  };

  /** Hit matrix delivered by import.php: { weste, name, targets } -> HitData */
  const hitsFromResponse = useCallback((payload: unknown): HitData[] | null => {
    const hits = (payload as { hits?: unknown })?.hits;
    if (!Array.isArray(hits) || hits.length === 0) return null;
    const mapped = hits
      .filter((h) => h && typeof h === 'object')
      .map((h) => {
        const row = h as { name?: unknown; targets?: unknown };
        const targets = (row.targets && typeof row.targets === 'object' ? row.targets : {}) as Record<string, number>;
        return { player: String(row.name ?? ''), targets };
      })
      .filter((h) => h.player);
    return mapped.length > 0 ? mapped : null;
  }, []);

  /** Stored hit data first, otherwise the hits field from the source API. */
  const loadHitData = useCallback((_gd: GameData, stored?: unknown) => {
    if (Array.isArray(stored) && stored.length > 0) {
      setHitData(stored as HitData[]);
      return;
    }
    setHitData(null);
  }, []);

  const fetchGameByNumber = useCallback(async (gameNumber: string): Promise<{ data: GameData; hits: HitData[] | null }> => {
    const paddedNumber = gameNumber.padStart(4, '0');
    const apiUrl = `https://lasertag-arena-siegen.de/highscores/assets/php/import.php?game=${paddedNumber}&_=${Date.now()}`;
    const raw = await fetchViaProxy(apiUrl);
    const parsed = parseJsonResponse(raw);
    const data = normalizeGameData(parsed);
    if (!data?.players || !data?.teams) throw new GameFetchError('not_found', parsed);
    return { data, hits: hitsFromResponse(parsed) };
  }, [fetchViaProxy, hitsFromResponse]);


  const saveHighscore = useCallback(async (data: GameData) => {
    const normalizedData = normalizeGameData(data);
    try {
      const response = await supabase.functions.invoke('save-highscore', {
        body: { game_data: normalizedData, game_id: normalizedData.game },
      });

      const { data: result, error } = response;

      if (error) {
        const errorStr = JSON.stringify(error) + (error.message || '') + (error.context?.body || '');
        if (isRateLimitError(errorStr)) return;
        console.error('Error calling save-highscore:', error);
        return;
      }

      if (result?.error) {
        if (isRateLimitError(result.error)) return;
        console.error('Failed to save highscore:', result.error);
        return;
      }

      if (result?.success) {
        toast({ title: "Highscore gespeichert!", description: `Spiel #${normalizedData.game} wurde erfolgreich gespeichert.` });
        loadSavedHighscores(false);
      }
    } catch (error: any) {
      const errorStr = String(error) + (error?.message || '') + JSON.stringify(error || {});
      if (isRateLimitError(errorStr)) return;
      console.error('Error in saveHighscore:', error);
    }
  }, [toast]);

  // --- Unified highscore loading ---

  const deduplicateAndSort = (data: any[]): SavedHighscore[] => {
    const gameMap = new Map<string, any>();
    (data || []).forEach((row: any) => {
      if (!gameMap.has(row.game_id)) gameMap.set(row.game_id, { ...row, game_data: normalizeGameData(row.game_data) });
    });

    return Array.from(gameMap.values())
      .sort((a, b) => {
        const timeA = parseGameDate(a.game_data).getTime();
        const timeB = parseGameDate(b.game_data).getTime();
        if (timeA !== timeB) return timeB - timeA;
        return parseInt(b.game_id) - parseInt(a.game_id);
      })
      .slice(0, RETENTION_GAMES) as SavedHighscore[];
  };

  const loadSavedHighscores = useCallback(async (isInitial: boolean = false) => {
    setIsRefreshing(true);
    try {
      // Read enough rows (we display max RETENTION_GAMES), sorted by when the game was actually played
      const { data, error } = await supabase
        .from('highscores')
        .select('id, game_id, game_data, game_started_at, hit_data')
        .order('game_started_at', { ascending: false })
        .limit(RETENTION_GAMES);

      if (error) { console.error('Error loading highscores:', error); return; }

      const uniqueList = deduplicateAndSort(data || []);
      setSavedHighscores(uniqueList);
      setLastUpdated(new Date());

      if (uniqueList.length > 0 && !manualSelectionRef.current) {
        if (isInitial) {
          // Sofort etwas anzeigen (schnell, ohne Proxy) …
          const gd = normalizeGameData(uniqueList[0].game_data);
          setGameData(gd);
          setLoading(false);
          loadHitData(gd, (uniqueList[0] as any).hit_data);
          // … danach die Importschnittstelle als maßgebliche Quelle prüfen:
          // ein noch nicht gespeichertes Spiel darf nicht verloren gehen.
          refreshLive();
        }
      } else if (isInitial && uniqueList.length === 0) {
        // No data in DB yet — fall back to live fetch once
        await fetchData();
      }

    } catch (error) {
      console.error('Error in loadSavedHighscores:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [hasManualSelection]);

  const fetchData = useCallback(async () => {
    try {
      const apiUrl = `https://lasertag-arena-siegen.de/highscores/assets/php/import.php?_=${Date.now()}`;
      const raw = await fetchViaProxy(apiUrl);
      const parsed = parseJsonResponse(raw);
      const data = normalizeGameData(parsed);

      if (!data?.players || !data?.teams) throw new Error('Unerwartetes Datenformat');

      const isNewGame = !gameDataRef.current || gameDataRef.current.game !== data.game;

      setGameData(data);
      setLoading(false);

      if (isNewGame) await saveHighscore(data);
      loadHitData(data, hitsFromResponse(parsed));
    } catch (error) {
      console.error('Failed to fetch game data:', error);

      try {
        const { data, error } = await supabase
          .from('highscores')
          .select('game_data, hit_data')
          .order('game_started_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!error && data) {
          const gd = normalizeGameData(data.game_data);
          setGameData(gd);
          loadHitData(gd, (data as any).hit_data);
        }
      } catch (dbError) {
        console.error('Failed to load fallback data:', dbError);
      }

      setLoading(false);
    }
  }, [fetchViaProxy, saveHighscore, loadHitData, hitsFromResponse]);

  /**
   * Live-Abgleich mit der Importschnittstelle. Sie ist die maßgebliche Quelle
   * für "das aktuellste Spiel" — die Datenbank kann hinterherhinken, weil der
   * Cron nur alle paar Minuten synchronisiert. Ein älteres Live-Ergebnis
   * überschreibt die Anzeige nicht.
   */
  const refreshLive = useCallback(async () => {
    if (manualSelectionRef.current) return;
    try {
      const apiUrl = `https://lasertag-arena-siegen.de/highscores/assets/php/import.php?_=${Date.now()}`;
      const raw = await fetchViaProxy(apiUrl);
      const parsed = parseJsonResponse(raw);
      const data = normalizeGameData(parsed);
      if (!data?.players?.length || !data?.teams) return;
      if (manualSelectionRef.current) return;

      const current = gameDataRef.current;
      if (current) {
        const tNew = parseGameDate(data).getTime();
        const tCur = parseGameDate(current).getTime();
        if (tNew < tCur) return; // Live-Antwort ist älter als das Angezeigte
        if (tNew === tCur && String(current.game) === String(data.game)) return; // unverändert
      }

      setGameData(data);
      setLoading(false);
      loadHitData(data, hitsFromResponse(parsed));
      await saveHighscore(data);
    } catch (error) {
      // Kein Toast: die DB-Anzeige steht bereits, ein Live-Fehler ist unkritisch.
      console.error('Live-Abgleich fehlgeschlagen:', error);
    }
  }, [fetchViaProxy, loadHitData, hitsFromResponse, saveHighscore]);


  const loadHighscoreById = useCallback(async (id: string) => {
    if (id === 'live') { fetchData(); return; }
    if (id === 'auto') return;

    // Try to serve from already-loaded list first (avoids DB roundtrip)
    const cached = savedHighscores.find(h => h.id === id);
    if (cached) {
      const gd = normalizeGameData(cached.game_data);
      setGameData(gd);
      loadHitData(gd, (cached as any).hit_data);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('highscores')
        .select('game_data, hit_data')
        .eq('id', id)
        .single();

      if (error) { setLoading(false); return; }

      const gd = normalizeGameData(data.game_data);
      setGameData(gd);
      setLoading(false);
      loadHitData(gd, (data as any).hit_data);
    } catch {
      setLoading(false);
    }
  }, [fetchData, loadHitData, savedHighscores]);

  const handleManualGameFetch = async () => {
    const gameNum = manualGameNumber.trim();
    if (!gameNum || !/^\d+$/.test(gameNum)) {
      if (gameNum) toast({ title: "Ungültige Spielnummer", description: "Bitte geben Sie nur Zahlen ein.", variant: "destructive" });
      return;
    }

    setLoadingManualGame(true);
    try {
      const { data, hits } = await fetchGameByNumber(gameNum);
      setGameData(data);
      setHasManualSelection(true);
      manualSelectionRef.current = true;
      await saveHighscore(data);
      loadHitData(data, hits);
      toast({ title: "Spiel geladen!", description: `Spiel #${data.game} wurde erfolgreich abgerufen und gespeichert.` });
      setManualGameNumber('');
    } catch (error) {
      // Technische Ursache nur in die Konsole, nie in den Toast.
      console.error('Manueller Spielabruf fehlgeschlagen:', error);
      const notFound = error instanceof GameFetchError && error.kind === 'not_found';
      toast({
        title: "Fehler beim Laden",
        description: notFound
          ? "Das gesuchte Spiel wurde nicht gefunden."
          : "Die Daten konnten gerade nicht geladen werden. Bitte versuche es in einem Moment erneut.",
        variant: "destructive",
      });

    } finally {
      setLoadingManualGame(false);
    }
  };

  const fetchBase64Image = async (url: string): Promise<string | null> => {
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return null;
      const blob = await res.blob();
      return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch { return null; }
  };

  const downloadPDF = async () => {
    if (!gameData) return;
    try {
      toast({ title: "PDF wird erstellt...", description: "Bitte warten Sie einen Moment." });
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
      const gutscheinBase64 = await fetchBase64Image('/gutschein.png');

      const response = await fetch(`${supabaseUrl}/functions/v1/generate-highscore-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${supabaseAnonKey}` },
        body: JSON.stringify({ game_data: gameData, hit_data: hitData, gutschein_image_base64: gutscheinBase64 || undefined }),
      });

      if (!response.ok) throw new Error('PDF generation failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `highscore-${gameData.game}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({ title: "PDF heruntergeladen!", description: "Das Highscore-PDF wurde erfolgreich heruntergeladen." });
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({ title: "Fehler", description: "PDF konnte nicht erstellt werden.", variant: "destructive" });
    }
  };

  // --- Effects ---

  // Deep link: ?game=<number> shows exactly that game. Archived games are
  // loaded from the database first — that gives us the original timestamp and
  // protects against the source system reusing the same game number with
  // different content. Only if the game is not stored locally do we fall back
  // to the source API.
  useEffect(() => {
    if (deepLinkHandled.current) return;
    deepLinkHandled.current = true;
    const param = new URLSearchParams(window.location.search).get('game');
    if (!param || !/^\d{1,4}$/.test(param)) return;

    const loadFromDb = async (gameNumber: string): Promise<boolean> => {
      try {
        const { data, error } = await supabase
          .from('highscores')
          .select('game_data, hit_data')
          .eq('game_id', gameNumber)
          .order('game_started_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (error || !data) return false;
        const gd = normalizeGameData(data.game_data);
        if (String(gd.game).replace(/^0+/, '') !== gameNumber.replace(/^0+/, '')) return false;
        setGameData(gd);
        setLoading(false);
        setHasManualSelection(true);
        manualSelectionRef.current = true;
        setSelectedHighscoreId(param);
        loadHitData(gd, (data as any).hit_data);
        return true;
      } catch {
        return false;
      }
    };

    const loadFromApi = async (gameNumber: string) => {
      setHasManualSelection(true);
      manualSelectionRef.current = true;
      setSelectedHighscoreId(param);
      setLoading(true);
      try {
        const { data, hits } = await fetchGameByNumber(gameNumber);
        setGameData(data);
        setLoading(false);
        loadHitData(data, hits);
      } catch {
        setLoading(false);
      }
    };

    loadFromDb(param).then((loaded) => {
      if (!loaded) loadFromApi(param);
    });
  }, [fetchGameByNumber, loadHitData]);

  // Keep the URL in sync with the displayed game (no reload). Only write
  // ?game= when the user actively selected a specific game; in live mode
  // the address bar stays clean.
  useEffect(() => {
    if (!gameData?.game) return;
    const url = new URL(window.location.href);
    if (manualSelectionRef.current) {
      url.searchParams.set('game', formatGameNumber(gameData.game));
    } else {
      url.searchParams.delete('game');
    }
    window.history.replaceState(null, '', url.toString());
  }, [gameData?.game]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible' && !hasManualSelection) {
        loadSavedHighscores(false);
        refreshLive();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [hasManualSelection, loadSavedHighscores, refreshLive]);

  useEffect(() => {
    // With a deep link active we only need the dropdown list, not the initial game
    loadSavedHighscores(!manualSelectionRef.current);
    // Poll every 2 minutes: Liste aus der DB + Live-Abgleich mit import.php,
    // damit ein noch nicht synchronisiertes Spiel sofort sichtbar wird.
    // Skip polling while the tab is hidden to save cloud requests.
    const interval = setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (manualSelectionRef.current) return;
      loadSavedHighscores(false);
      refreshLive();
    }, 120000);
    return () => clearInterval(interval);
  }, [hasManualSelection]);


  useEffect(() => {
    // On initial mount the select shows "Ältere Spiele aufrufen" (value 'live')
    // but the newest game is displayed from DB — skip the live proxy fetch.
    if (selectedHighscoreId === 'live' && !hasManualSelection) return;
    loadHighscoreById(selectedHighscoreId);
  }, [selectedHighscoreId]);

  // Falls der aktive Reiter gerade entfernt wurde (z.B. "Teams" bei Spielen
  // ohne echte Teams), auf Rangliste zurückfallen.
  useEffect(() => {
    const currentHasTeams = gameData?.teams && gameData.teams.length > 1;
    if (activeSection === 'teams' && !currentHasTeams) {
      setActiveSection('ranking');
    }
  }, [activeSection, gameData?.teams]);

  // --- Render ---

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto" />
          <p className="text-xl text-primary">Lade Highscores...</p>
        </div>
      </div>
    );
  }

  if (!gameData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl text-muted-foreground">Keine Daten verfügbar</p>
      </div>
    );
  }

  if (showTournamentMode) {
    return (
      <Suspense fallback={
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      }>
        <TournamentMode onClose={() => setShowTournamentMode(false)} />
      </Suspense>
    );
  }

  const latestGameId = savedHighscores[0]?.game_data?.game;
  const isLatestGame = !hasManualSelection || (!!latestGameId && String(latestGameId) === String(gameData.game));

  const shareUrl = `${window.location.origin}${window.location.pathname}?game=${formatGameNumber(gameData.game)}`;

  const backToLive = () => {
    setHasManualSelection(false);
    manualSelectionRef.current = false;
    const url = new URL(window.location.href);
    url.searchParams.delete('game');
    window.history.replaceState(null, '', url.toString());
    setSelectedHighscoreId('live');
    loadSavedHighscores(true);
  };

  const sortedPlayers = [...gameData.players].sort((a, b) => parseInt(b.score) - parseInt(a.score));
  const playerStats = computePlayerStats(gameData.players, hitData, gameData.teams);


  const sectionVisibility = (key: 'ranking' | 'hits' | 'teams' | 'records') =>
    activeSection === key ? 'block' : 'hidden lg:block';

  const actionPill =
    'h-10 flex-shrink-0 rounded-full border border-border bg-card text-foreground hover:bg-card-strong hover:text-foreground shadow-none';

  const hasTeams = gameData.teams && gameData.teams.length > 1;
  const timeParts = parseTimestampParts(gameData.timestamp);

  const mobileTabs: Array<{ key: 'ranking' | 'hits' | 'teams' | 'records'; label: string }> = [
    { key: 'ranking', label: 'Rangliste' },
    { key: 'hits', label: 'Treffer' },
    { key: 'records', label: 'Rekorde' },
  ];
  if (hasTeams) mobileTabs.splice(2, 0, { key: 'teams', label: 'Teams' });

  return (
    <div className="min-h-screen pb-10">
      <Confetti />

      <div className="mx-auto w-full max-w-[960px] space-y-4 px-4 py-4 sm:px-6 sm:py-6">
        {/* Kopfbereich */}
        <header className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p className="text-overline text-primary">Lasertag Arena Siegen</p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <h1 className="text-[28px] font-bold leading-none tracking-[-0.02em] sm:text-[34px]">
                <span className="sr-only">Lasertag Siegen Highscores – Arena Siegen, </span>
                Spiel #{formatGameNumber(gameData.game)}
              </h1>
              {isLatestGame ? (
                <span className="text-overline inline-flex items-center gap-1.5 rounded-full bg-destructive px-2.5 py-1 text-destructive-foreground">
                  <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
                  Live
                </span>
              ) : (
                <span className="text-overline inline-flex items-center rounded-full border border-border bg-card px-2.5 py-1 text-muted-foreground">
                  Archiv
                </span>
              )}
            </div>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {[timeParts.date, timeParts.time, 'Arena Siegen'].filter(Boolean).join(' · ')}
            </p>
          </div>

          <div className="-mx-4 flex items-center gap-2 overflow-x-auto px-4 no-scrollbar lg:mx-0 lg:flex-wrap lg:justify-end lg:overflow-visible lg:px-0">
            <ShareButtons gameNumber={gameData.game} shareUrl={shareUrl} />

            <Select
              value={selectedHighscoreId}
              onValueChange={(value) => {
                setSelectedHighscoreId(value);
                setHasManualSelection(true);
                manualSelectionRef.current = true;
              }}
            >
              <SelectTrigger className={`${actionPill} w-[190px] px-4`} aria-label="Highscore-Spiel auswählen">
                <SelectValue placeholder="Wähle einen Highscore" />
              </SelectTrigger>
              <SelectContent className="z-50 bg-popover">
                <SelectItem value="live">Ältere Spiele anzeigen</SelectItem>
                {savedHighscores.map((highscore) => (
                  <SelectItem key={highscore.id} value={highscore.id}>
                    Spiel #{formatGameNumber(highscore.game_data.game)} - {highscore.game_data.timestamp}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button onClick={downloadPDF} variant="outline" className={`${actionPill} gap-2 px-4`}>
              <Download className="h-4 w-4" />
              <span>Download</span>
            </Button>
            <Button onClick={() => setShowTournamentMode(true)} variant="outline" className={`${actionPill} gap-2 px-4`}>
              <Crown className="h-4 w-4" />
              <span className="whitespace-nowrap">Turnier Auswertung</span>
            </Button>
          </div>
        </header>

        {/* Älteres Spiel abrufen */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            type="text"
            inputMode="numeric"
            placeholder="Älteres Spiel abrufen – z.B. 817"
            aria-label="Älteres Spiel abrufen"
            value={manualGameNumber}
            onChange={(e) => setManualGameNumber(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && handleManualGameFetch()}
            className="h-10 rounded-full border-border bg-card pl-10 pr-24 text-foreground"
            maxLength={4}
          />
          <Button
            onClick={handleManualGameFetch}
            disabled={loadingManualGame || !manualGameNumber.trim()}
            variant="ghost"
            className="absolute right-1 top-1/2 h-8 -translate-y-1/2 rounded-full px-4 text-sm"
          >
            {loadingManualGame ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Laden'}
          </Button>
        </div>

        {/* Team-Duell */}
        {hasTeams && <TeamRankings teams={gameData.teams} variant="duel" />}

        {/* Umschalter (nur unterhalb von 1024px) */}
        <div className="-mx-4 overflow-x-auto px-4 no-scrollbar lg:hidden">
          <div className="flex min-w-max gap-1 rounded-full bg-surface-elevated p-1" role="tablist">
            {mobileTabs.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                role="tab"
                aria-selected={activeSection === key}
                onClick={() => setActiveSection(key)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  activeSection === key
                    ? 'bg-primary font-bold text-primary-foreground'
                    : 'bg-transparent text-muted-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Spieler-Rangliste */}
        <section className={sectionVisibility('ranking')}>
          <h2 className="text-lg font-bold">Spieler Rankings</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            Tippe auf einen Spieler, um seine Detailstatistiken zu sehen.
          </p>
          <div className="mt-3 space-y-2">
            {sortedPlayers.map((player, index) => (
              <PlayerCard
                key={`${player.weste}-${player.name}`}
                player={player}
                rank={index + 1}
                index={index}
                stats={playerStats.get(`${player.weste}-${player.name}`)}
                photoUrl={buildPlayerPhotoUrl(gameData.game_start, gameData.game, player.weste)}
              />
            ))}
          </div>

        </section>

        {/* Treffertabelle */}
        <section className={sectionVisibility('hits')}>
          {hitData && hitData.length > 0 ? (
            <HitMatrix hitData={hitData} players={gameData.players} />
          ) : (
            <div className="rounded-2xl border border-border bg-card p-3 text-center">
              <p className="text-sm text-muted-foreground">
                Für dieses Spiel sind keine Trefferdaten verfügbar.
              </p>
            </div>
          )}
        </section>

        {/* Teams */}
        {hasTeams && (
          <section className={sectionVisibility('teams')}>
            <TeamRankings teams={gameData.teams} variant="list" />
          </section>
        )}

        {/* Rekorde */}
        <section className={sectionVisibility('records')}>
          <RecordsSection savedHighscores={savedHighscores} />
        </section>



        {/* Refresh status */}
        <div className="text-center text-sm text-muted-foreground">
          {hasManualSelection ? (
            <div className="flex flex-col items-center justify-center gap-2 sm:flex-row">
              <span>Archiviertes Spiel – keine automatische Aktualisierung</span>
              <Button variant="outline" size="sm" onClick={backToLive} className="rounded-full border-border bg-card">
                Zurück zum aktuellen Spiel
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-center space-x-2">
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} aria-hidden="true" />
              <span>
                {lastUpdated
                  ? `Zuletzt aktualisiert: ${lastUpdated.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
                  : 'Automatische Aktualisierung alle 2 Minuten'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
